/****************************************************
 * Module Name:  DnkInvoiceReport.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import renderCellExpand from "../common/dataGridCellExpand";
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import dayjs from "dayjs";
import { Box, CircularProgress, styled, Tooltip } from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonSnackbar from "../common/commonSnackBar";
import DnkProxy from "../../utility/proxy/manager/DnkProxy";


const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({

}));

export default function DnkInvoiceReport({ loadedCustomers,
    defaultfromdate,
    defaulttodate, }) {
    const [listData, setListData] = useState([]);
    const [salesListLoader, setSalesListLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [columns, setColumns] = useState([]);


    useEffect(() => {
        handleSearch(defaultfromdate, defaulttodate);
    }, [])

    const handleSearch = (fromDate, toDate) => {
        setSalesListLoader(true);
        let customerid = loadedCustomers.customer[0].value;
        let data = {
            from: (fromDate !== null) ? dayjs(defaultfromdate).format('YYYY-MM-DD') + "T00:00:00.000Z" : null,
            to: (toDate !== null) ? dayjs(defaulttodate).format('YYYY-MM-DD') + "T23:59:59.999Z" : null,
        };
        DnkProxy.GetInvoiceDataByCustomerid(customerid, data, (response) => {
            if (response) {
                const uniqueFilterValues = {
                    completedEmp: Utility.getDistinctOptions(response, "completedEmp"),
                    customercompanyname: Utility.getDistinctOptions(response, "customercompanyname"),
                };
                prepareColumns(uniqueFilterValues);
                setListData(response);
                setSalesListLoader(false);
            } else {
                setSalesListLoader(false);
                setErrorSnackbar(true);
            }
        });
    };

    const prepareColumns = (uniqueFilterValues) => {
        let columnNames = [];
        const columnData = initialColumns();

        columnData.forEach(iCol => {
            if (iCol.field === "completedEmp") {
                iCol.type = "singleSelect";
                iCol.valueOptions = uniqueFilterValues ? uniqueFilterValues.completedEmp : [];
                iCol.filterOperators = Utility.combinedFilters();
            }
            if (iCol.field === "customercompanyname") {
                iCol.type = "singleSelect";
                iCol.valueOptions = uniqueFilterValues ? uniqueFilterValues.customercompanyname : [];
                iCol.filterOperators = Utility.combinedFilters();
            }
            columnNames.push(iCol);
        });
        setColumns(columnNames);
    }
    const initialColumns = () => [

        {
            field: "orderNo",
            headerName: "ORDER NO",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.orderNo)
        },
        {
            field: "stoneId",
            headerName: "DNK STONEID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.stoneId)
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            width: 40,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "right",
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        {
            field: "certificateNo",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "polish",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "rapValue",
            headerName: "RAP",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "originalDis",
            headerName: "ORG DIS",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "offerDis",
            headerName: "OFFER DIS",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "originalPPC",
            headerName: "ORG PPC",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "offerPPC",
            headerName: "OFFER PPC",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "originalFinalAmt",
            headerName: "ORG TOTAL",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "offerFinalAmt",
            headerName: "OFFER TOTAL",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "finalOfferDis",
            headerName: "OFFER TOTAL",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "finalOfferRate",
            headerName: "OFFER TOTAL",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "finalOfferAmt",
            headerName: "OFFER TOTAL",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            width: 100,
        },
        {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "completeddate",
            headerName: "DATE",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "completedEmp",
            headerName: "EMP",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "company",
            headerName: "COMPANY",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "customercompanyname",
            headerName: "CUST COMPANY",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
    ];
    return (
        <div className="background">
            <Box px={1}>
                <CommonSnackbar
                    show={errorSnackbar}
                    message={Constants.APiFailErrorMessage}
                    variant={"error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={"Failed"}
                    handleClose={() => setErrorSnackbar(false)}
                />
            </Box>
            <Box
                sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                        marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-cc8tf1": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    height: "73vh"
                }}
            >
                <StyledDataGrid
                    disableRowSelectionOnClick={true}
                    localeText={{ noRowsLabel: "No Record Found" }}
                    sx={{ fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight, }}
                    slots={{
                        toolbar: GridToolbar,
                    }}
                    rows={listData}
                    density="compact"
                    columns={columns}
                    getRowId={(row) => row.stoneId}
                    loading={salesListLoader}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide columns id, the other columns will remain visible
                            },
                        },
                        pagination: {
                            paginationModel: Constants.pageSize,
                        },
                    }}
                    pageSizeOptions={Constants.pageSizeOptions}
                    pagination
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: {
                                debounceMs: 500,
                            },
                        },
                    }}
                />
                <Box
                    style={{
                        position: "absolute",
                        top: 5,
                        right: "270px",
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    {salesListLoader ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                onClick={() => handleSearch(defaultfromdate, defaulttodate)}
                                color="primary"
                                fontSize="medium"
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}
                </Box>

            </Box>
        </div>
    );
};


